/* eslint-disable react/prop-types */

import { useEffect, useState, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiDropzone from "components/SuiDropzone";
import SuiTypography from "components/SuiTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { Media } from "api";

let selectedFiles = [];

function MediaLibraryUpload({mode}) {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState({});
  const history = useHistory();
  const dzRef = useRef(null);

  useEffect(() => {
    selectedFiles = [];
  }, []);

  const uploadFile = (file) => {
    const n = uploadResults;
    const promise = Media.upload(file, file.size);
    n[file.upload.uuid] = { file, promise };
    setUploadResults(n);
    promise
      .then((result) => {
        if (result) {
          uploadResults[file.upload.uuid] = { file, promise, result };
        } else {
          uploadResults[file.upload.uuid] = { file, promise, error: true };
        }
        setUploadResults(_.clone(uploadResults, true));
      })
      .catch((err) => {
        uploadResults[file.upload.uuid] = { file, promise, error: err };
        setUploadResults(_.clone(uploadResults, true));
      });
    return promise;
  };

  const dataTableData = useMemo(() => {
    const result = {
      columns: [
        {
          Header: t("IMAGE"),
          accessor: "image",
          width: "1%",
          Cell: ({ value }) => (
            <img
              alt={value}
              src={value}
              style={{
                maxWidth: "100px",
                maxHeight: "100px",
              }}
            />
          ),
        },
        { Header: t("FILE NAME"), accessor: "filename" },
        { Header: t("STATUS"), accessor: "status" },
        { Header: t("REMARK"), accessor: "link" },
        {
          Header: "",
          accessor: "action",
        },
      ],
      rows: [],
    };
    Object.entries(uploadResults).forEach(([, uploadResult]) => {
      const isSuccess = !!uploadResult.result;
      const isError = !!uploadResult.error;
      let color = "text";
      let status = "Uploading";
      let url = "";

      if (isSuccess) {
        url = _.get(uploadResult, "result.data.s3ObjKey.Location");
        color = "success";
        status = "Uploaded";
      } else if (isError) {
        color = "error";
        status = "Failed";
      }

      result.rows.push({
        image: _.get(uploadResult, "file.dataURL", ""),
        filename: _.get(uploadResult, "file.name", ""),
        status: (
          <SuiTypography variant="caption" color={color}>
            {status}
          </SuiTypography>
        ),
        link: isSuccess ? (
          <a href={url} target="_blank" rel="noreferrer">
            <SuiTypography variant="caption">{url}</SuiTypography>
          </a>
        ) : null,
        action: url ? (
          <SuiButton
            variant="gradient"
            color="info"
            onClick={() => {
              navigator.clipboard.writeText(url);
            }}
          >
            {t("COPY LINK")}
          </SuiButton>
        ) : null,
      });
    });
    return result;
  }, [uploadResults]);

  return (
    <>
      <Helmet>
        <title>{t("Upload")}</title>
      </Helmet>
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox my={1.5}>
          <Grid container>
            <Grid item xs={12}>
              <Card>
                <SuiBox p={3}>

                  {mode!=="view" && (isUploading ? (
                    <SuiBox display="flex" justifyContent="space-between">
                      <SuiBox display="flex">
                        <SuiButton
                          onClick={() => {
                            history.goBack();
                          }}
                        >
                          {t("BACK")}
                        </SuiButton>
                      </SuiBox>
                      {/* <SuiBox display="flex" justifyContent="flex-end">
                      <SuiButton variant="gradient" color="info">
                        EXPORT EXCEL
                      </SuiButton>
                    </SuiBox> */}
                    </SuiBox>
                  ) : (
                    <SuiButton
                      variant="gradient"
                      color="info"
                      onClick={async () => {
                        if (selectedFiles.length) {
                          const accepteds = dzRef.current.getAcceptedFiles();
                          const uploads = {};
                          accepteds.forEach((file) => {
                            uploads[file.upload.uuid] = { file };
                            uploadFile(file);
                          });
                          setIsUploading(true);
                          setUploadResults(uploads);
                        }
                      }}
                    >
                      {t("Upload")}
                    </SuiButton>
                  ))}
                </SuiBox>
                <SuiBox>
                  <Divider />
                  {!isUploading ? (
                    <SuiBox p={3}>
                      <SuiBox>
                        <Grid container>
                          <Grid item xs={12}>
                            <SuiDropzone
                              ref={dzRef}
                              options={{
                                autoProcessQueue: false,
                                accept: (file, done) => {
                                  if (file.type.indexOf("image") !== 0) {
                                    done("File type not accepted.");
                                  } else if (file.size > 1024 * 1024 * 10) {
                                    // 10MB
                                    done("File size too large.");
                                  } else {
                                    done();
                                  }
                                },
                                addedfiles: (files) => {
                                  selectedFiles = files;
                                },
                                dictDefaultMessage: t("Drop files here to upload")
                              }}
                            />
                          </Grid>
                        </Grid>
                      </SuiBox>
                    </SuiBox>
                  ) : (
                    <>
                      <Grid item>
                        <DataTable
                          table={dataTableData}
                          entriesPerPage={{
                            defaultValue: 9999,
                          }}
                          showTotalEntries={false}
                          isSorted={false}
                          showEntriesPerPageCaption={false}
                        />
                      </Grid>
                    </>
                  )}
                </SuiBox>
              </Card>
            </Grid>
          </Grid>
        </SuiBox>
        <Footer />
      </DashboardLayout>
    </>
  );
}

export default MediaLibraryUpload;
